import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Menu, X, Siren } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { PHONE_DISPLAY, TEL_LINK, waLink, DEFAULT_MSG, NAV_LINKS } from "@/constants/site";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <div className="sticky top-0 z-50">
        <a
          href={TEL_LINK}
          data-testid="topbar-emergency-call-button"
          className="group flex items-center justify-center gap-2 sm:gap-3 bg-emergency px-4 py-2.5 text-white transition-colors duration-300 hover:bg-emergency-dark"
        >
          <Siren className="h-4 w-4 shrink-0" />
          <span className="font-mono text-[11px] sm:text-xs font-bold tracking-[0.18em] uppercase">
            Emergency Dispatch: <span className="underline underline-offset-2">{PHONE_DISPLAY}</span> — Tap to Call
          </span>
        </a>

        <nav className="bg-charcoal/95 backdrop-blur-xl border-b border-white/10">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 py-3">
            <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
              <img
                src="/logo.webp"
                alt="Paliway Towing logo"
                className="h-11 w-11 rounded-full ring-2 ring-emergency/70 object-cover bg-white"
              />
              <span className="leading-none">
                <span className="block font-display text-xl font-black uppercase tracking-wide text-white">
                  Paliway <span className="text-emergency">Towing</span>
                </span>
                <span className="block font-mono text-[10px] tracking-[0.25em] text-zinc-400 uppercase mt-1">
                  Tarrant County, TX
                </span>
              </span>
            </Link>

            <div className="hidden lg:flex items-center gap-7">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
                      isActive ? "text-emergency" : "text-zinc-300 hover:text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>

            <div className="flex items-center gap-2.5">
              <a
                href={TEL_LINK}
                data-testid="header-call-now-button"
                className="hidden sm:inline-flex items-center gap-2 bg-emergency px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-emergency-dark hover:-translate-y-0.5"
              >
                <Phone className="h-4 w-4" /> Call Now
              </a>
              <a
                href={waLink(DEFAULT_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-whatsapp-button"
                className="hidden sm:inline-flex items-center gap-2 bg-dispatch px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-dispatch-dark hover:-translate-y-0.5"
              >
                <FaWhatsapp className="h-4 w-4" /> Text / WhatsApp
              </a>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                data-testid="mobile-menu-button"
                className="lg:hidden inline-flex h-10 w-10 items-center justify-center border border-white/15 text-white"
                aria-label="Toggle menu"
              >
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </nav>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-asphalt/98 backdrop-blur-lg lg:hidden"
            data-testid="mobile-menu-overlay"
          >
            <div className="flex h-full flex-col justify-center gap-2 px-8 pt-24">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-3 font-display text-4xl font-black uppercase tracking-tight ${
                        isActive ? "text-emergency" : "text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="mt-8 flex flex-col gap-3"
              >
                <a
                  href={TEL_LINK}
                  data-testid="mobile-menu-call-button"
                  className="flex items-center justify-center gap-2 bg-emergency py-4 font-display text-lg font-bold uppercase tracking-wider text-white"
                >
                  <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
                </a>
                <a
                  href={waLink(DEFAULT_MSG)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-menu-whatsapp-button"
                  className="flex items-center justify-center gap-2 bg-dispatch py-4 font-display text-lg font-bold uppercase tracking-wider text-white"
                >
                  <FaWhatsapp className="h-5 w-5" /> Text / WhatsApp Us
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
